import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { TextEditorState } from '../textEditor/textEditorState';
import { input, inputGroup, InputGroupState, InputState } from '../input';
import { notifyError, notifyLoading, notifySuccess } from '../../services/notifications';
import { JobModel } from '../../entities';
import { BlockDiff } from '../textEditor/suggestions/blockDiffTypes';

// public_safety.py abandons a revision by clearing publicSafetyReportRevising and stamping
// one of these. The poll below cannot tell that apart from success on the flag alone, so a
// code missing from this set reloads the unchanged report and announces it as revised.
const REVISION_FAILURE_ERRORS = new Set([
  'PublicSafetyReportRevisionSourceMissing',
  'PublicSafetyReportRevisionIncomplete',
]);

// The backend refuses an edit the caller lacks permission for by throwing `Unauthorized`, which
// runGraphQlApiRequest turns into a NotAuthorized ApiError. Reporting that as "try again later"
// sent a Benton County deputy chasing his network for a week and a half while every save was
// being rejected on his team role -- retrying can never clear it, so say so.
//
// `type` alone is not enough: ApiService also returns NotAuthorized when it cannot build an auth
// context at all (an expired session, before any request is sent). Telling THAT user to go ask
// their library owner sends them down the same wrong path in the opposite direction -- their
// retry-after-signing-in genuinely would have worked. ApiError only carries a GraphQL response
// when the inner error was a ClientError, i.e. the backend answered and refused, so require it.
function reportErrorMessage(err: unknown, fallback: string): string {
  const apiError = err as { type?: string; originalErrorResponse?: unknown } | null;
  return apiError?.type === 'NotAuthorized' && !!apiError.originalErrorResponse
    ? 'You do not have permission to edit this report. Ask your library owner for edit access.'
    : fallback;
}

type SuggestionCategory = {
  label: string;
  blockIndices: number[];
};

export class EditorWindowState
  extends StoreNode {

  readonly nodeType = 'EditorWindow';
  readonly publicSafetyReportComplete: InputState;
  readonly publicSafetyReportFeedback: InputState;
  readonly reviseEnabled: InputState;
  readonly submitButton: InputState;
  readonly inputGroup: InputGroupState;

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    this.window.listen(
      this.windowListener);

      this.publicSafetyReportComplete = input(this, {
        name: 'publicSafetyReportComplete',
        isRequired: () => {
          // Certification finalizes a review, but NOT when requesting a revise.
          return this.mode === 'report' && !(this.canReviseReport && this.reviseEnabled.value);
        }
      });

      this.reviseEnabled = input(this, {
        name: 'reviseEnabled',
        label: 'Re-run this report incorporating this feedback',
      });

      this.publicSafetyReportFeedback = input(this, {
        name: 'publicSafetyReportFeedback',
        label: 'How can we make your next first draft better?',
        placeholder: 'Share ideas, preferences, or examples…',
        error: (input, fallback) =>
          (input.value && input.value.length < 5) ? 'Add at least 5 characters' : fallback,
        showStatusMessage: true,
      });
  
      this.submitButton = input(this, {
        name: 'saveButton',
        disabled: (input: InputState) => {
          return (
            !!input.loading ||
            input.isSubmitting ||
            this.inputGroup.hasTouchedErrorInputs ||
            !this.inputGroup.hasChangedInputs);
        }
      });
  
      this.inputGroup = inputGroup(this, {
        name: 'draftReview',
        inputs: [
          this.publicSafetyReportComplete,
          this.publicSafetyReportFeedback,
          this.reviseEnabled,
          this.submitButton
        ],
        isSubmitDisabled: (form) => {
          return form.hasErrorInputs || !form.hasChangedInputs;
        }
      });
  }

  readonly window = new WindowState(this.store, { closeOnOutsideClick: false });
  readonly editor = new TextEditorState(this.store);
  
  @observable isLoading = false;
  @observable data: string | null = null;
  @observable fileName: string | null = null;
  @observable jobId: string | null = null;
  @observable aiDraftContent: string | null = null;
  @observable isManualReport: boolean = false;
  @observable isReadOnly: boolean = false;
  @observable isRevising = false;

  // This is a stupid workaround and has to be removed
  @observable mode: 'notes' | 'report' | 'dualPlusReport' | 'translation' | null = null;
  @observable editorMode: 'suggestions' | 'collaboration' = 'collaboration';

  @observable suggestionDetail: number | null = null;
  @observable maxDetail: number = 1;
  @observable categories: SuggestionCategory[] = [];
  @observable reportDiff: BlockDiff[] | null = null;
  @observable isDiffLoading: boolean = false;
  @observable isDiffAvailable: boolean | null = null;
  @observable originalContent: string | null = null;

  @computed get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  @computed get canReviseReport(): boolean {
    return this.mode === 'report' && !this.isManualReport;
  }

  @computed get contentMatchesAiDraft(): boolean {
    if (!this.originalContent || !this.aiDraftContent) return false;
    const toText = (html: string) => {
      // Replace <br> with spaces (textContent skips self-closing elements)
      // and add spaces after block-closing tags so textContent doesn't
      // concatenate text across boundaries without separation.
      const spaced = html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|h[1-6]|li|div|blockquote|tr|td|th|ul|ol)>/gi, '</$1> ');
      const el = document.createElement('div');
      el.innerHTML = spaced;
      return (el.textContent ?? '').replace(/[\s\u00a0]+/g, ' ').trim();
    };
    return toText(this.originalContent) === toText(this.aiDraftContent);
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        if (this.isLoading)
          return;
        this.close();
        break;
    }
  }

  @action
  open({ jobId, data, fileName, mode, aiDraftContent, editorMode, isManualReport, readOnly }: { jobId?: string, data?: string, fileName?: string, mode?: 'notes' | 'report' | 'dualPlusReport' | 'translation', aiDraftContent?: string, editorMode?: 'suggestions' | 'collaboration', isManualReport?: boolean, readOnly?: boolean }) {
    this.jobId = jobId ?? null;
    this.editor.setJobId(jobId ?? null);
    this.editor.setBranding(null);
    // Best effort and unawaited: the editor opens on the plain surface and repaints if the job's
    // custom report format turns out to carry the customer's page furniture.
    void this.editor.loadBranding(this.job?.id);
    this.data = data ?? null;
    this.fileName = fileName ?? null;
    this.mode = mode ?? null;
    this.aiDraftContent = aiDraftContent ?? null;
    this.isManualReport = isManualReport ?? false;
    this.isReadOnly = readOnly ?? false;
    this.editorMode = editorMode ?? 'collaboration';
    this.originalContent = data ?? null;
    this.reportDiff = null;
    this.isDiffLoading = false;
    this.isDiffAvailable = null;
    this.suggestionDetail = null;
    this.categories = [];
    this.dispatch('Overlays', 'openWindow', { name: 'EditorWindow' });
    this.emit('open');
    this.window.open();

    this.publicSafetyReportComplete.loadValue(this.job?.publicSafetyReportComplete);
  }

  @action
  onSave = async (content?: string): Promise<boolean> => {
    if (this.mode === 'translation') return false;

    try {
      const [, err] = await this.store.apiUpdateJob({
          id: this.jobId || '',
          ...(this.mode === 'notes'
            ? { publicSafetyNotes: content }
            : {}),
          ...(this.mode === 'report'
            ? (this.isManualReport
              ? { publicSafetyManualReport: content }
              : { publicSafetyReport: content })
            : {}),
          ...(this.mode === 'dualPlusReport'
            ? { publicSafetyDualPlusReport: content }
            : {}),
      });

      if (err) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, reportErrorMessage(err, 'Could not update the report. Try again later.'));
        return false;
      }

      // After saving a manual report, reset diff state so suggestions
      // are recomputed from scratch on next "Show AI Suggestions" click
      if (this.mode === 'report' && this.isManualReport && content) {
        runInAction(() => {
          this.originalContent = content;
          this.reportDiff = null;
          this.isDiffAvailable = null;
          this.suggestionDetail = null;
          this.maxDetail = 1;
          this.categories = [];
        });
      }

      return true;
    } catch (_e) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Could not update job. Try again later.');
      return false;
    }
  }

  submit = async () => {
    this.inputGroup.handleSubmit();
    if (this.inputGroup.error) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Fix the validation errors before saving.');
      return;
    }

    if (this.canReviseReport && this.reviseEnabled.value) {
      await this.revise();
      return;
    }

    try {
      notifyLoading(this, `Submitting review...`);

      const [, err] = await this.store.apiUpdateJob({
          id: this.jobId || '',
          publicSafetyNotesFeedback: this.mode === 'notes' ?  (this.publicSafetyReportFeedback.value || '') : undefined,
          publicSafetyReportFeedback: this.mode === 'report' ?  (this.publicSafetyReportFeedback.value || '') : undefined,
          ...(this.mode === 'report' ? {
            publicSafetyReportComplete: !!this.publicSafetyReportComplete.value,
          } : {}),
          // No report body here on purpose. submit() calls inputGroup.handleSubmit() above, which
          // cascades to every child input and clears isChangedSinceLastSubmit, so a branch gated on
          // that flag could never run -- certifying has only ever sent the flag and the feedback.
          // The body reaches S3 through onSave, which writes publicSafetyManualReport or
          // publicSafetyReport according to isManualReport. Making certify persist the body is a
          // product decision (it would start writing on a path that has never written), not a
          // mechanical fix.
      });

      if (err) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, reportErrorMessage(err, 'Could not update the report state. Try again later.'));
        return;
      }

      this.inputGroup.handleSubmitResolve();
      this.publicSafetyReportFeedback.reset();
      notifySuccess(this, `Review submitted successfully.`);

    } catch (_e) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Could not submit your review. Try again later.');
      return;
    }
  }

  @action
  revise = async () => {
    const feedback = (this.publicSafetyReportFeedback.value || '').trim();
    if (feedback.length < 5) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Add at least 5 characters of feedback to revise.');
      return;
    }
    if (this.editor.hasUnresolvedSuggestions) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Accept or reject track changes before revising.');
      return;
    }

    try {
      notifyLoading(this, 'Starting report revision…');
      if (!await this.editor.saveNow()) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, 'Could not save the current report. Revision was not started.');
        return;
      }
      const [, err] = await this.store.apiReviseReport({ jobId: this.jobId || '', feedback });
      if (err) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, reportErrorMessage(err, 'Could not start the revision. Try again later.'));
        return;
      }

      runInAction(() => { this.isRevising = true; });
      this.inputGroup.handleSubmitResolve();
      notifySuccess(this, 'Revision started — generating your updated report…');

      await this.pollUntilRevised();
    } catch (_e) {
      runInAction(() => { this.isRevising = false; });
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Could not start the revision. Try again later.');
    }
  }

  // Default 5s; overridable to 0 in tests so the poll loop does not use a real timer.
  pollIntervalMs = 5000;

  private pollUntilRevised = async () => {
    const MAX_POLLS = 120; // ~10 min ceiling — public-safety Lambda timeout is 900s

    for (let i = 0; i < MAX_POLLS; i++) {
      if (this.pollIntervalMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, this.pollIntervalMs));
      }

      // Narrow status probe — avoid re-pulling the full report/related jobs every tick.
      const [res, err] = await this.store.api.runQuery('getJob_checkReportRevising', { id: this.jobId || '' });
      if (err) continue;
      // The worker clears publicSafetyReportRevising ONLY in the same write that persists a
      // regenerated report — a failed run leaves it set — so a cleared (falsy) flag is the
      // success edge. Still running: keep polling.
      if (res?.getJob?.publicSafetyReportRevising) continue;

      // Ready: pull the heavyweight report once and refresh the editor.
      const [, fetchErr] = await this.store.jobManager.apiFetchJob(this.jobId || '');
      if (fetchErr) continue;
      if (!this.job?.publicSafetyReport) {
        runInAction(() => { this.isRevising = false; });
        notifyError(this, 'The revision finished without producing a report. Try again later.');
        return;
      }

      // A failed revision usually DOES still have a report — the previous one. The worker
      // clears the revising flag on its way out and stamps ingestError, so the flag alone
      // cannot tell failure from success; without this the editor reloads the unchanged
      // report and announces it as revised.
      if (REVISION_FAILURE_ERRORS.has(this.job.ingestError as string)) {
        runInAction(() => { this.isRevising = false; });
        this.inputGroup.handleSubmitReject();
        notifyError(this, 'Report revision failed. Try again.');
        return;
      }

      runInAction(() => { this.isRevising = false; });
      this.reloadReportContent();
      this.publicSafetyReportFeedback.reset();
      this.reviseEnabled.loadValue(false);
      notifySuccess(this, 'Your revised report is ready.');
      return;
    }

    runInAction(() => { this.isRevising = false; });
    notifyError(this, 'Revision is taking longer than expected. Refresh to check again.');
  }

  @action
  private reloadReportContent = () => {
    const content = this.job?.publicSafetyReport ?? null;
    this.data = content;
    this.originalContent = content;
    if (content) {
      this.editor.resetToOriginalContent(content);
    }
    // Suggestion/diff state was computed against the old report — invalidate it.
    this.reportDiff = null;
    this.isDiffAvailable = null;
    this.suggestionDetail = null;
    this.maxDetail = 1;
    this.categories = [];
  }

  @action
  setSuggestionDetail(detail: number) {
    this.suggestionDetail = Math.max(1, Math.min(this.maxDetail, detail));
  }

  @action
  acceptAllSuggestions = async () => {
    // If not at max detail, fetch full diff first so ALL suggestions
    // are loaded into the editor before accepting.
    if (this.suggestionDetail !== null && this.suggestionDetail < this.maxDetail) {
      this.suggestionDetail = this.maxDetail;
      await this.fetchReportDiff();
      // Wait for React useEffect to propagate reportDiff to TextEditorState
      await new Promise(r => setTimeout(r, 50));
    }

    // Accept all track change suggestions in the editor
    this.editor.acceptAllSuggestions();

    // Save the accepted content to the backend
    const content = this.editor.editorModel as string;
    await this.onSave(content);

    // Reset diff state — next "Show AI Suggestions" will find no changes
    runInAction(() => {
      this.originalContent = content;
      this.reportDiff = null;
      this.isDiffAvailable = false;
      this.suggestionDetail = null;
      this.maxDetail = 1;
      this.categories = [];

      // Restore clean editor state with accepted content
      this.editor.resetToOriginalContent(content);
    });
  }

  @action
  clearManualReport = async () => {
    if (!this.jobId) return;

    const [, err] = await this.store.apiUpdateJob({
      id: this.jobId,
      publicSafetyManualReport: '',
    });

    if (err) {
      notifyError(this, 'Could not clear the report. Try again later.');
      return;
    }

    runInAction(() => {
      this.originalContent = null;
      this.reportDiff = null;
      this.isDiffAvailable = null;
      this.suggestionDetail = null;
      this.maxDetail = 1;
      this.categories = [];
      this.editor.resetToOriginalContent('');
    });

    notifySuccess(this, 'Report cleared.');
  }

  @action
  hideDiff = () => {
    this.reportDiff = null;
    this.isDiffAvailable = null;
    this.suggestionDetail = null;
    this.maxDetail = 1;
    this.categories = [];
    // Restore original content in editor
    if (this.originalContent) {
      this.editor.resetToOriginalContent(this.originalContent);
    }
  }

  @action
  fetchReportDiff = async () => {
    if (!this.jobId) return;

    this.isDiffLoading = true;

    try {
      const [res, err] = await this.store.apiService.getJobReportDiff({
        id: this.jobId,
        ...(this.suggestionDetail !== null ? { suggestionDetail: this.suggestionDetail } : {}),
      });

      if (err) {
        runInAction(() => {
          this.isDiffLoading = false;
          this.isDiffAvailable = false;
        });
        return;
      }

      const diffJson = res?.getJob?.publicSafetyReportDiff ?? null;

      runInAction(() => {
        if (diffJson === null) {
          this.isDiffAvailable = false;
          this.isDiffLoading = false;
          return;
        }

        try {
          const parsed = JSON.parse(diffJson);
          // Support both new shape { diffs, maxDetail } and legacy BlockDiff[]
          let diffs: BlockDiff[];
          if (Array.isArray(parsed)) {
            diffs = parsed as BlockDiff[];
            this.maxDetail = 1;
          } else {
            diffs = parsed.diffs as BlockDiff[];
            this.maxDetail = parsed.maxDetail ?? 1;
            this.categories = (parsed.categories ?? []) as SuggestionCategory[];
            // On first load, use backend-computed default
            if (this.suggestionDetail === null) {
              this.suggestionDetail = parsed.defaultDetail ?? 1;
            }
          }

          // No actual changes — report matches the AI draft
          const hasChanges = diffs.some(d => d.type !== 'unchanged');
          if (!hasChanges) {
            this.isDiffAvailable = false;
            this.isDiffLoading = false;
            return;
          }

          this.reportDiff = diffs;
          this.isDiffAvailable = true;
        } catch {
          this.isDiffAvailable = false;
        }

        this.isDiffLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isDiffLoading = false;
        this.isDiffAvailable = false;
      });
    }
  }

  @action
  close() {
    this.reset();
    this.dispatch('Overlays', 'closeWindow');
    this.window.close();
    this.emit('close');
  }

  @action
  private reset() {
    this.isLoading = false;
    this.jobId = null;
    this.mode = null;
    this.editorMode = 'collaboration';
    this.fileName = null;
    this.data = null;
    this.aiDraftContent = null;
    this.suggestionDetail = null;
    this.maxDetail = 1;
    this.categories = [];
    this.reportDiff = null;
    this.isDiffLoading = false;
    this.isDiffAvailable = null;
    this.originalContent = null;
    this.reviseEnabled.reset();
    this.isRevising = false;
  }
}
